import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Collapse,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  MoreVert as MoreIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

// Get initials from username
const getInitials = (name) => {
  if (!name || name === 'Anonymous') return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Single comment component
function Comment({
  comment,
  currentUserId,
  onReply,
  onDelete,
  onReport,
  isReply = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isOwner = comment.user_id === currentUserId;

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) onDelete(comment);
  };

  const handleReport = () => {
    handleMenuClose();
    if (onReport) onReport(comment);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.5,
        pl: isReply ? 4 : 0,
      }}
    >
      <Avatar
        sx={{
          width: isReply ? 28 : 32,
          height: isReply ? 28 : 32,
          bgcolor: 'primary.main',
          fontSize: isReply ? '0.7rem' : '0.8rem',
        }}
      >
        {comment.is_anonymous ? <PersonIcon /> : getInitials(comment.username)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            {comment.is_anonymous ? 'Anonymous' : comment.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(comment.created_at)}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {comment.content}
        </Typography>

        {/* Reply button (only for top-level comments) */}
        {!isReply && onReply && (
          <Button
            size="small"
            startIcon={<ReplyIcon sx={{ fontSize: 14 }} />}
            onClick={() => onReply(comment)}
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.25,
              minHeight: 'auto',
              fontSize: '0.75rem',
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'primary.main',
              },
            }}
          >
            Reply
          </Button>
        )}
      </Box>

      {/* Menu */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{ alignSelf: 'flex-start', mt: -0.5 }}
      >
        <MoreIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isOwner ? (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        ) : (
          <MenuItem onClick={handleReport}>
            <ListItemIcon>
              <FlagIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Report" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// Main CommentsSection component
function CommentsSection({
  prayerId,
  comments = [],
  currentUserId,
  onAddComment,
  onDeleteComment,
  onReportComment,
  loading = false,
  maxVisible = 3,
  placeholder = "Write a comment...",
}) {
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Group comments with replies
  const topLevelComments = comments.filter(c => !c.parent_id);
  const repliesByParent = comments
    .filter(c => c.parent_id)
    .reduce((acc, comment) => {
      if (!acc[comment.parent_id]) acc[comment.parent_id] = [];
      acc[comment.parent_id].push(comment);
      return acc;
    }, {});

  const visibleComments = showAll ? topLevelComments : topLevelComments.slice(0, maxVisible);
  const hasMore = topLevelComments.length > maxVisible;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await onAddComment({
        prayer_id: prayerId,
        content: newComment.trim(),
        parent_id: replyingTo?.id || null,
      });
      setNewComment('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (comment) => {
    setReplyingTo(comment);
    inputRef.current?.focus();
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  useEffect(() => {
    if (replyingTo) {
      inputRef.current?.focus();
    }
  }, [replyingTo]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Comment input */}
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
        {replyingTo && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <ReplyIcon fontSize="small" color="primary" />
            <Typography variant="caption" sx={{ flex: 1 }}>
              Replying to <strong>{replyingTo.username}</strong>
            </Typography>
            <Button size="small" onClick={handleCancelReply}>
              Cancel
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            inputRef={inputRef}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={replyingTo ? `Reply to ${replyingTo.username}...` : placeholder}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            maxRows={4}
            disabled={submitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
          />
          <IconButton
            type="submit"
            color="primary"
            disabled={!newComment.trim() || submitting}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Comments list */}
      {!loading && visibleComments.length > 0 && (
        <Box>
          <Divider sx={{ mb: 1 }} />

          {visibleComments.map((comment) => (
            <Box key={comment.id}>
              <Comment
                comment={comment}
                currentUserId={currentUserId}
                onReply={handleReply}
                onDelete={onDeleteComment}
                onReport={onReportComment}
              />

              {/* Replies */}
              {repliesByParent[comment.id]?.map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  currentUserId={currentUserId}
                  onDelete={onDeleteComment}
                  onReport={onReportComment}
                  isReply
                />
              ))}
            </Box>
          ))}

          {/* Show more/less button */}
          {hasMore && (
            <Button
              size="small"
              onClick={() => setShowAll(!showAll)}
              endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                mt: 1,
                color: 'text.secondary',
                textTransform: 'none',
              }}
            >
              {showAll
                ? 'Show less'
                : `View ${topLevelComments.length - maxVisible} more comment${topLevelComments.length - maxVisible > 1 ? 's' : ''}`
              }
            </Button>
          )}
        </Box>
      )}

      {/* Empty state */}
      {!loading && comments.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 2 }}
        >
          No comments yet. Be the first to share your thoughts.
        </Typography>
      )}
    </Box>
  );
}

export default CommentsSection;
